import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useSnapshot } from 'valtio';

import type { Field, FieldId, VisibilityTypeId } from '#types/System.types';

import { formStore, PageScopeContext } from '../store';
import {
    acquireEntityRecordStore,
    releaseEntityRecordStore,
    EMPTY_ENTITY_RECORD_STATE,
} from '../store/entityRecord/entityRecordStore';
import { useVisibilityMode } from './useVisibilityMode';

export type UseFieldUIScopeReturn = {
    insideForm: boolean;
    insideEntity: boolean;
    formId: string | undefined;
    versionId: string | undefined;
    entityId: string | undefined;
    recordId: string | undefined;
    visibilityMode: VisibilityTypeId;
    fieldValue: unknown;
    /** True while the layout editor's mock scope owns this leaf — a read-only surface. */
    isPreview: boolean;
    /**
     * Read the current `Field` for `fieldId` at call time. Resolves the same
     * three branches as `fieldValue` but off the live Valtio proxy — it does
     * NOT subscribe — so an undo conflict check or a save closure sees the
     * persisted value without bloating render-time tracking.
     */
    getField: () => Field | undefined;
};

/**
 * Narrow scope reader for `useFieldUI`.
 *
 * Subscribes only to the specific field's data plus the page-level identifiers
 * the field needs. Avoids the broad `useFormStore()` / `useEntityRecordScope()`
 * subscriptions that re-render every field on any data write.
 *
 * Which artifact owns `fieldValue` is positional: the nearest `PageScope`
 * decides, so a form field and an entity-sidebar field rendering in the same
 * commit read different documents. `getField` resolves the same three branches
 * off the live proxy for callers that must not subscribe.
 */
export const useFieldUIScope = (fieldId: FieldId): UseFieldUIScopeReturn => {
    const pageScope = useSnapshot(useContext(PageScopeContext));
    const visibilityMode = useVisibilityMode();

    const formSnap = useSnapshot(formStore.state);

    const entityPreview = pageScope.source === 'preview' ? pageScope.preview : null;
    const scopedEntityId = pageScope.source === 'entity' ? pageScope.entityId : '';
    const scopedRecordId = pageScope.source === 'entity' ? pageScope.recordId : undefined;
    const isLiveEntityBound = scopedEntityId !== '';

    const entityEntry = useMemo(
        () => (isLiveEntityBound ? acquireEntityRecordStore(scopedEntityId, scopedRecordId) : null),
        [isLiveEntityBound, scopedEntityId, scopedRecordId],
    );

    useEffect(() => {
        if (!isLiveEntityBound) return;
        return () => {
            releaseEntityRecordStore(scopedEntityId, scopedRecordId);
        };
    }, [isLiveEntityBound, scopedEntityId, scopedRecordId]);

    const entitySnap = useSnapshot(entityEntry?.state ?? EMPTY_ENTITY_RECORD_STATE);

    const insideForm = formSnap.formId !== undefined;
    const insideEntity = entityPreview ? entityPreview.insideEntityRecordContext : isLiveEntityBound;

    const fieldValue = entityPreview
        ? entityPreview.entityRecord.data?.fields?.[fieldId]?.value
        : isLiveEntityBound
            ? entitySnap.entityRecord.data?.fields?.[fieldId]?.value
            : formSnap.formVersion.data?.fields?.[fieldId]?.value;

    const getField = useCallback((): Field | undefined => {
        if (entityPreview) {
            return entityPreview.entityRecord.data?.fields?.[fieldId] as Field | undefined;
        }
        if (entityEntry) {
            return entityEntry.state.entityRecord.data?.fields?.[fieldId] as Field | undefined;
        }
        return formStore.state.formVersion.data?.fields?.[fieldId] as Field | undefined;
    }, [fieldId, entityPreview, entityEntry]);

    return {
        insideForm,
        insideEntity,
        formId: formSnap.formId,
        versionId: formSnap.versionId,
        entityId: entityPreview?.entityId ?? (isLiveEntityBound ? scopedEntityId : undefined),
        recordId: entityPreview?.recordId ?? (isLiveEntityBound ? scopedRecordId : undefined),
        visibilityMode,
        fieldValue,
        isPreview: entityPreview !== null,
        getField,
    };
};
